// external dependencies
import { isEmpty } from "@swiggy-private/js-utils";
import { unwrapResponseWithCode, ApiResponse } from "@swiggy-private/core-utils";

import {
    GlobalHeaderState,
    HTTP_METHODS,
    makeDownstreamApiCall,
    Response,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";
import { MARKETPLACE_BUSINESS_LINE } from "@swiggy-private/payment-types";
import { PaymentFallbacksApiResponse } from "@swiggy-private/payment-ui";

// internal dependencies
import { getPaymentMethodsParamsByBusinessLine, Headers } from "@payment-client/helpers/PublicApiHelper";
import { prepareCreateTxnParams } from "@payment-client/helpers/publicPaymentHelper";
import * as PaymentApi from "@payment-client/interfaces/PaymentApi";
import { GetPaymentMethodsParams, GetPaymentOptionsParams } from "@payment-client/interfaces/PaymentApiPublic";
import { CreatePennyTransactionParams, CreateTransactionParams } from "@payment-client/interfaces/PaymentPublic";
import { SplitPayMethodValues } from "@payment-client/interfaces/SplitPay";
import * as UpiApi from "@payment-client/interfaces/UpiApi";
import { BannerParams } from "@payment-client/interfaces/Banner";
import { unwrapResponse } from "@payment-client/utils/Api";
import * as PennyTxnApi from "@swiggy-private/payment-types";
import { DOWNSTREAM_HOSTS } from "@payment-client/constants/Hosts";

// local imports
import {
    getBanksList,
    getPaymentFallbackMethods,
    checkTransactionStatus,
    createPennyTransaction,
    confirmPennyTransaction,
    updateFrequencyCounter,
    verifyVpa,
} from "./publicPaymentService";

export const GET_PAYMENT_OPTIONS_URI = "/api/v3/payment/get-payment-options";
export const GET_SWIGGY_SUPER_PAYMENTS_URI = "/api/v3/swiggy-super/get-payment-options";

const getSwiggyPaymentOptions = async (
    apiHost: string,
    params: GetPaymentMethodsParams,
    headers: Headers,
): Promise<PaymentApi.GetPaymentMethodsResponse> => {
    const data = {
        subscriptionItems: [
            {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                plan_id: params.planId,
                quantity: params.quantity,
            },
        ],
        // eslint-disable-next-line @typescript-eslint/naming-convention
        address_id: headers?.addressid,
    };

    const result = unwrapResponseWithCode<{ cart: object; payments: PaymentApi.GetPaymentMethodsResponse }>(
        (await makeDownstreamApiCall<Response<{ cart: object; payments: PaymentApi.GetPaymentMethodsResponse }>>({
            apiHost,
            endpoint: GET_SWIGGY_SUPER_PAYMENTS_URI,
            bodyParams: data,
            options: { method: HTTP_METHODS.POST },
        })) as ApiResponse<{ cart: object; payments: PaymentApi.GetPaymentMethodsResponse }>,
    );

    return result.toPromise().then((data) => data.payments);
};

const getPaymentOptionsWithCartKey = async (
    apiHost: string,
    params: GetPaymentOptionsParams,
    _headers: Headers,
): Promise<PaymentApi.GetPaymentMethodsResponse> => {
    const result = unwrapResponseWithCode<PaymentApi.GetPaymentMethodsResponse>(
        (await makeDownstreamApiCall<Response<PaymentApi.GetPaymentMethodsResponse>>({
            apiHost,
            endpoint: GET_PAYMENT_OPTIONS_URI,
            bodyParams: params as unknown as { [key: string]: unknown },
            options: { method: HTTP_METHODS.POST },
            /* eslint-disable @typescript-eslint/naming-convention -- API/backend field names for NR */
            extraNRData: {
                turbo_variant: params.turbo_variant,
                split_pay_methods: params.split_pay_methods,
                is_user_eligible_for_single_click: params.is_user_eligible_for_single_click,
                without_split_pay: params.without_split_pay,
                is_user_cred_eligible: params.is_user_cred_eligible,
                is_inapp_upi_eligible: params.is_inapp_upi_eligible,
                is_gifting_flow: params.is_gifting_flow,
                bill_to_company_toggle: params.bill_to_company_toggle,
                card_details: params.card_details,
                is_bill_to_company_selected: params.is_bill_to_company_selected,
                isScenesFullPaymentFlow: params.isScenesFullPaymentFlow,
            },
        })) as ApiResponse<PaymentApi.GetPaymentMethodsResponse>,
    );

    return result.toPromise();
};

const getPaymentOptions = async (
    apiHost: string,
    params: GetPaymentMethodsParams,
    _headers: Headers,
): Promise<PaymentApi.GetPaymentMethodsResponse> => {
    const result = unwrapResponseWithCode<PaymentApi.GetPaymentMethodsResponse>(
        (await makeDownstreamApiCall<Response<PaymentApi.GetPaymentMethodsResponse>>({
            apiHost,
            endpoint: GET_PAYMENT_OPTIONS_URI,
            bodyParams: params as unknown as { [key: string]: unknown },
            options: { method: HTTP_METHODS.POST },
            /* eslint-disable @typescript-eslint/naming-convention -- API/backend field names for NR */
            extraNRData: {
                turbo_variant: params.turbo_variant,
                split_pay_methods: params.split_pay_methods,
                is_user_eligible_for_single_click: params.is_user_eligible_for_single_click,
                without_split_pay: params.without_split_pay,
                is_user_cred_eligible: params.is_user_cred_eligible,
                is_inapp_upi_eligible: params.is_inapp_upi_eligible,
                is_gifting_flow: params.is_gifting_flow,
                bill_to_company_toggle: params.bill_to_company_toggle,
                card_details: params.card_details,
                is_bill_to_company_selected: params.is_bill_to_company_selected,
                isScenesFullPaymentFlow: params.isScenesFullPaymentFlow,
            },
        })) as ApiResponse<PaymentApi.GetPaymentMethodsResponse>,
    );

    return result.toPromise();
};

const getSwiggyMoneyPaymentMethods = async (
    apiHost: string,
    params: GetPaymentMethodsParams,
    _headers: Headers,
): Promise<PaymentApi.GetPaymentMethodsResponse> => {
    const result = unwrapResponseWithCode<PaymentApi.GetPaymentMethodsResponse>(
        (await makeDownstreamApiCall<Response<PaymentApi.GetPaymentMethodsResponse>>({
            apiHost,
            endpoint: GET_PAYMENT_OPTIONS_URI,
            bodyParams: params as unknown as { [key: string]: unknown },
            options: { method: HTTP_METHODS.POST },
            extraNRData: {
                turbo_variant: params.turbo_variant,
                split_pay_methods: params.split_pay_methods,
                is_user_eligible_for_single_click: params.is_user_eligible_for_single_click,
                without_split_pay: params.without_split_pay,
                is_user_cred_eligible: params.is_user_cred_eligible,
                is_inapp_upi_eligible: params.is_inapp_upi_eligible,
                is_gifting_flow: params.is_gifting_flow,
                bill_to_company_toggle: params.bill_to_company_toggle,
                card_details: params.card_details,
                is_bill_to_company_selected: params.is_bill_to_company_selected,
                isScenesFullPaymentFlow: params.isScenesFullPaymentFlow,
            },
        })) as ApiResponse<PaymentApi.GetPaymentMethodsResponse>,
    );

    return result.toPromise();
};

const getCBCCFDPaymentMethods = async (
    apiHost: string,
    params: GetPaymentMethodsParams,
    _headers: Headers,
): Promise<PaymentApi.GetPaymentMethodsResponse> => {
    const result = unwrapResponseWithCode<PaymentApi.GetPaymentMethodsResponse>(
        (await makeDownstreamApiCall<Response<PaymentApi.GetPaymentMethodsResponse>>({
            apiHost,
            endpoint: GET_PAYMENT_OPTIONS_URI,
            bodyParams: params as unknown as { [key: string]: unknown },
            options: { method: HTTP_METHODS.POST },
            extraNRData: {
                turbo_variant: params.turbo_variant,
                split_pay_methods: params.split_pay_methods,
                is_user_eligible_for_single_click: params.is_user_eligible_for_single_click,
                without_split_pay: params.without_split_pay,
                is_user_cred_eligible: params.is_user_cred_eligible,
                is_inapp_upi_eligible: params.is_inapp_upi_eligible,
                is_gifting_flow: params.is_gifting_flow,
                bill_to_company_toggle: params.bill_to_company_toggle,
                card_details: params.card_details,
                is_bill_to_company_selected: params.is_bill_to_company_selected,
                isScenesFullPaymentFlow: params.isScenesFullPaymentFlow,
            },
        })) as ApiResponse<PaymentApi.GetPaymentMethodsResponse>,
    );

    return result.toPromise();
};

export async function getPaymentMethodsPublic(
    apiHost: string,
    queryParams: {
        isUserCredEligible: boolean;
        splitPayMethods?: string;
        isUserEligibleForSingleClick: boolean;
        isGiftingFlow: boolean;
        isInAppUpiEligible: boolean;
        turboVariant?: string;
        isBillToCompanySelected?: boolean;
        billToCompanyInfoMeta?: {
            attendeeEmail: string[];
        };
    },
): Promise<PaymentApi.GetPaymentMethodsResponse> {
    const headers = GlobalHeaderState.getHeaders();
    const businessLine =
        GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine) ?? MARKETPLACE_BUSINESS_LINE.REGULAR;

    /* eslint-disable @typescript-eslint/naming-convention */
    const params: GetPaymentMethodsParams = {
        address_id: "",
        card_details: true,
        is_user_cred_eligible: queryParams.isUserCredEligible || false,
        is_user_eligible_for_single_click: queryParams.isUserEligibleForSingleClick || false,
        without_split_pay: false,
        split_pay_methods: queryParams.splitPayMethods || SplitPayMethodValues.NONE,
        is_gifting_flow: queryParams.isGiftingFlow || false,
        is_inapp_upi_eligible: queryParams.isInAppUpiEligible || false,
        bill_to_company_toggle: queryParams.isBillToCompanySelected || false,
        ...(queryParams.billToCompanyInfoMeta && {
            billToCompanyInfoMeta: queryParams.billToCompanyInfoMeta,
        }),
    };

    if (!isEmpty(queryParams?.turboVariant)) {
        params["turbo_variant"] = queryParams.turboVariant;
    }

    const [finalParams, _headers] = getPaymentMethodsParamsByBusinessLine(businessLine, params, headers);

    if (businessLine === MARKETPLACE_BUSINESS_LINE.SWIGGY_SUPER) {
        return getSwiggyPaymentOptions(apiHost, finalParams, _headers);
    }

    if (
        businessLine === MARKETPLACE_BUSINESS_LINE.DASH ||
        businessLine === MARKETPLACE_BUSINESS_LINE.POST_ORDER_TIP ||
        businessLine === MARKETPLACE_BUSINESS_LINE.DINEOUT ||
        businessLine === MARKETPLACE_BUSINESS_LINE.PAY_ON_DELIVERY ||
        businessLine === MARKETPLACE_BUSINESS_LINE.HANDPICKED ||
        businessLine === MARKETPLACE_BUSINESS_LINE.ASSURE
    ) {
        return getPaymentOptionsWithCartKey(apiHost, finalParams as GetPaymentOptionsParams, _headers);
    }

    if (businessLine === MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY) {
        return getSwiggyMoneyPaymentMethods(apiHost, finalParams, _headers);
    }

    if (businessLine === MARKETPLACE_BUSINESS_LINE.CBCC) {
        return getCBCCFDPaymentMethods(apiHost, finalParams, _headers);
    }

    if (businessLine === MARKETPLACE_BUSINESS_LINE.SWIGGY_ONE) {
        return getPaymentOptions(apiHost, finalParams, _headers);
    }

    return getPaymentOptions(apiHost, finalParams, _headers);
}

export async function getBanksListPublic(apiHost: string): Promise<PaymentApi.BankListData> {
    const response = await getBanksList(apiHost);
    return unwrapResponseWithCode(response).toPromise();
}

export async function getPaymentFallbackMethodsPublic(
    apiHost: string,
    orderId: string,
): Promise<PaymentFallbacksApiResponse> {
    const headers = GlobalHeaderState.getHeaders();
    const cartKey = (headers["cartkey"] as string) || "";
    const response = await getPaymentFallbackMethods(apiHost, { orderId, cartKey });
    return unwrapResponse(response).toPromise();
}

export async function verifyVpaPublic(apiHost: string, vpaAddress: string): Promise<UpiApi.VerfiyVpaAddressResponse> {
    const response = await verifyVpa(apiHost, { payerVpa: vpaAddress });

    return unwrapResponse(response).unwrapOrElse(() => ({
        invalidReason: "Some Error occurred.",
        vpaValid: false,
    }));
}

export async function checkTransactionStatusPublic(
    apiHost: string,
    transactionId: string,
    paymentCode?: string,
): Promise<string> {
    const response = await checkTransactionStatus(apiHost, transactionId, paymentCode);
    const result = await unwrapResponse(response).toPromise();
    return result as string;
}

export async function createPennyTransactionPublic(
    apiHost: string,
    params: CreatePennyTransactionParams & { [key: string]: unknown },
): Promise<PennyTxnApi.CreatePennyTransactionResponse> {
    const requestParams = prepareCreateTxnParams(params);
    return createPennyTransaction(apiHost, requestParams as CreateTransactionParams & { [key: string]: unknown });
}

export async function confirmPennyTransactionPublic(
    apiHost: string,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    params: { order_id: string },
): Promise<ApiResponse<PennyTxnApi.ConfirmPennyTransactionResponse>> {
    const response = await confirmPennyTransaction(apiHost, params);
    return response;
}

export async function updateFrequencyCounterPublic(apiHost: string, params: BannerParams): Promise<void> {
    const response = await updateFrequencyCounter(
        DOWNSTREAM_HOSTS.USER_SERVICE_HOST,
        params as unknown as BannerParams & { [key: string]: unknown },
    );
    unwrapResponse(response).toPromise();
}
